import React, { useMemo, useState, useEffect } from "react";
import useOrganisationUsers from "../../hooks/useOrganisationUsers";
import useRoles from "../../hooks/useRoles";
import useCreateOrgUser from "../../hooks/useCreateOrgUser";
import Loader from "../../components/common/GlobalLoader";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const emptyForm = {
  name: "",
  email: "",
  password: "",
  role: "",
  roleId: "",
  companyName: "",
  billingAddress: "",
  phone: "",
  canCreateSalesOrders: false,
  canViewPrices: false,
  canViewDashboard: false,
  canViewItems: false,
  canViewAllOrders: false,
  canViewOrderedItems: false,
  canViewMyAccount: false,
  canManageUsers: false,
  canManageRolesAndPermissions: false,
  isActive: true,
};

const UserManagement = () => {
  const { users, loading, saving, error, addUser, updateUser, removeUser, fetchUsers } =
    useOrganisationUsers();

  const {
    roles,
    permissionsCatalog,
    loading: rolesLoading,
    error: rolesError,
  } = useRoles();

  // Determine the current logged-in user's role (from localStorage)
  let currentUserRoleName = "";
  let isCustomerAdminUser = false;

  try {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      const role = parsedUser?.role;

      if (typeof role === "string") {
        currentUserRoleName = role;
      } else if (role && typeof role === "object") {
        currentUserRoleName = role.name || role.role || "";
      }

      const upperRole = (currentUserRoleName || "").toUpperCase().replace(/\s+/g, "_");
      isCustomerAdminUser =
        upperRole === "CUSTOMER_ADMIN" || upperRole === "CUSTOMERADMIN";
    }
  } catch (err) {
    // Fail silently if parsing localStorage fails
    // eslint-disable-next-line no-console
    console.error("Failed to read current user role from localStorage:", err);
  }

  // Filter roles that can be assigned by the current user
  const assignableRoles = useMemo(() => {
    if (!roles || roles.length === 0) return [];

    return roles.filter((role) => {
      const name = (role.name || "").toUpperCase().replace(/\s+/g, "_");

      const isSuperAdminRole =
        name === "SUPER_ADMIN" || name === "SUPERADMIN";
      const isCustomerAdminRole =
        name === "CUSTOMER_ADMIN" || name === "CUSTOMERADMIN";

      // Super Admin role must NEVER be shown in the frontend
      if (isSuperAdminRole) return false;

      // When logged in as Customer Admin, hide the Customer Admin role as well
      if (isCustomerAdminUser && isCustomerAdminRole) return false;

      return true;
    });
  }, [roles, isCustomerAdminUser]);

  const {
    createOrgUser,
    loading: creatingOrgUser,
    error: createOrgUserError,
  } = useCreateOrgUser();

  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [accessDialog, setAccessDialog] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Show only organisation sub‑users (exclude the main organisation owner)
  const visibleUsers = useMemo(
    () => users.filter((u) => !!u.organizationOwner),
    [users]
  );

  // Group permissions for better organization
  const actionPermissions = useMemo(
    () =>
      permissionsCatalog.filter(
        (p) => p.key === "canCreateSalesOrders" || p.key === "canViewPrices"
      ),
    [permissionsCatalog]
  );
  const modulePermissions = useMemo(
    () =>
      permissionsCatalog.filter(
        (p) => p.key !== "canCreateSalesOrders" && p.key !== "canViewPrices"
      ),
    [permissionsCatalog]
  );

  // Update form permissions when roleId is selected
  useEffect(() => {
    if (form.roleId && roles.length > 0) {
      const selectedRole = roles.find((r) => r._id === form.roleId);
      if (selectedRole && selectedRole.permissions) {
        setForm((prev) => ({
          ...prev,
          ...selectedRole.permissions,
        }));
      }
    } else if (!form.roleId) {
      // Reset permissions when no role is selected
      setForm((prev) => ({
        ...prev,
        canCreateSalesOrders: false,
        canViewPrices: false,
        canViewDashboard: false,
        canViewItems: false,
        canViewAllOrders: false,
        canViewOrderedItems: false,
        canViewMyAccount: false,
        canManageUsers: false,
        canManageRolesAndPermissions: false,
      }));
    }
  }, [form.roleId, roles]);

  const superAdminCount = useMemo(
    () =>
      users.filter(
        (u) =>
          u.role === "Super Admin" ||
          u.role === "SUPER_ADMIN" ||
          u.isSuperAdmin
      ).length,
    [users]
  );

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      alert("Please fill in all required fields.");
      return;
    }
    if (!form.role) {
      alert("Please select a role.");
      return;
    }

    setCreating(true);
    try {
      const payload = {
        email: form.email,
        password: form.password,
        CustomerName: form.name,
        CompanyName: form.companyName,
        BillingAddress: form.billingAddress,
        phone: form.phone,
        role: form.roleId || form.role,
        isAccessEnabled: form.isActive,
      };

      await createOrgUser(payload);

      // Refresh users list to get the real user with MongoDB ObjectId from API
      await fetchUsers();
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          "Failed to create organisation user. Please try again."
      );
    }
    setCreating(false);
    // Reset form to empty state
    setForm({
      name: "",
      email: "",
      password: "",
      role: "",
      roleId: "",
      companyName: "",
      billingAddress: "",
      phone: "",
      canCreateSalesOrders: false,
      canViewPrices: false,
      canViewDashboard: false,
      canViewItems: false,
      canViewAllOrders: false,
      canViewOrderedItems: false,
      canViewMyAccount: false,
      canManageUsers: false,
      canManageRolesAndPermissions: false,
      isActive: true,
    });
    setShowPassword(false);
  };

  const handleToggle = (user, field) => {
    const nextStatus = !user[field];

    // For access enable/disable, show confirmation dialog first
    if (field === "isActive") {
      setAccessDialog({
        userId: user._id,
        userName: user.name || user.CustomerName || user.email,
        userEmail: user.email,
        nextStatus,
      });
      return;
    }

    updateUser(user._id, { [field]: nextStatus });
  };

  // Helper to get current role id for a user
  const getCurrentRoleId = (user) => {
    if (!roles || roles.length === 0) return "";
    if (!user.role) return "";

    let roleValue = user.role;

    if (typeof user.role === "object" && user.role !== null) {
      roleValue = user.role._id || user.role.name || "";
    }

    if (!roleValue) return "";

    let role = roles.find((r) => r._id === roleValue);

    if (!role) {
      role = roles.find((r) => r.name === roleValue);
    }

    return role ? role._id : "";
  };

  const handleRoleChange = async (user, newRoleId) => {
    const currentRoleId = getCurrentRoleId(user);
    if (!newRoleId || newRoleId === currentRoleId) return;
    await updateUser(user._id, { role: newRoleId });
  };

  const handleRemove = (user) => {
    const isSuper =
      user.role === "Super Admin" ||
      user.role === "SUPER_ADMIN" ||
      user.isSuperAdmin;

    if (isSuper && superAdminCount <= 1) {
      alert(
        "You must have at least one Super Admin for your organisation."
      );
      return;
    }

    // Open confirmation dialog instead of window.confirm
    setDeleteDialog({
      userId: user._id,
      userName: user.name || user.CustomerName || user.email,
      userEmail: user.email,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog) return;
    await removeUser(deleteDialog.userId);
    setDeleteDialog(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialog(null);
  };

  const isBusy =
    loading || saving || creating || rolesLoading || creatingOrgUser;

  return (
    <div className="relative">
      {isBusy && (
        <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-lg z-40">
          <Loader />
        </div>
      )}

      <div
        className={`bg-white border rounded-xl p-4 sm:p-6 shadow-md max-w-7xl mx-auto ${
          isBusy ? "opacity-40 pointer-events-none select-none" : ""
        }`}
      >
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold theme-text-primary">
              User Management
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Invite users to your organisation, enable or disable access and
              manage their capabilities.
            </p>
          </div>
          <div className="text-sm text-gray-600">
            <span className="font-medium">
              Super Admins: {superAdminCount}
            </span>
          </div>
        </div>

        {(error || createOrgUserError || rolesError) && (
          <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error || createOrgUserError || rolesError}
          </div>
        )}
        {successMessage && (
          <div className="mb-4 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700">
            {successMessage}
          </div>
        )}

        {/* Invite / Add User */}
        <section className="mb-8 border rounded-lg p-4 sm:p-5 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">
            Invite a New User
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mb-4">
            Inviting a user will create or update the corresponding Zoho
            Inventory contact and trigger the standard portal invitation email
            (handled by the backend).
          </p>

          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                placeholder="e.g. Jane Smith"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                placeholder="user@yourcompany.com"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  className="w-full border rounded-md px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <FaEyeSlash className="w-4 h-4" />
                  ) : (
                    <FaEye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Company Name
              </label>
              <input
                type="text"
                value={form.companyName}
                onChange={(e) => handleChange("companyName", e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                placeholder="e.g. Ayshire Drinks"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Billing Address
              </label>
              <input
                type="text"
                value={form.billingAddress}
                onChange={(e) => handleChange("billingAddress", e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                placeholder="e.g. 123 Street, City"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                placeholder="e.g. 03001234567"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Initial Role <span className="text-red-500">*</span>
              </label>
              <select
                value={form.roleId}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedRole = roles.find(
                    (role) => role._id === selectedId
                  );
                  handleChange("roleId", selectedId);
                  handleChange("role", selectedRole ? selectedRole.name : "");
                }}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                required
                disabled={rolesLoading}
              >
                <option value="">
                  {rolesLoading ? "Loading roles..." : "Select a role..."}
                </option>
                {assignableRoles.length === 0 && !rolesLoading && (
                  <option value="" disabled>
                    No assignable roles available
                  </option>
                )}
                {assignableRoles.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.name}
                    {role.isDefault ? " (Default)" : ""}
                  </option>
                ))}
              </select>
              {rolesLoading && (
                <p className="mt-1 text-xs text-gray-500">
                  Loading roles...
                </p>
              )}
              {!rolesLoading && assignableRoles.length === 0 && (
                <p className="mt-1 text-xs text-red-500">
                  No assignable roles found. Please create roles first.
                </p>
              )}
              {!rolesLoading && assignableRoles.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  Permissions will be automatically set based on the selected role.
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <span className="text-xs font-medium text-gray-700 mb-2 block">
                Access Controls (Based on Selected Role)
              </span>
              <div className="bg-white border rounded-lg p-4 space-y-4">
                {/* Action Permissions */}
                <div>
                  <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                    Action Permissions
                  </h5>
                  <div className="space-y-2">
                    {actionPermissions.map((perm) => (
                      <label
                        key={perm.key}
                        className="flex items-center gap-2 text-sm text-gray-700"
                      >
                        <input
                          type="checkbox"
                          checked={!!form[perm.key]}
                          disabled
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 opacity-60 cursor-not-allowed"
                        />
                        <span className={form.role ? "" : "text-gray-400"}>
                          {perm.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Module Access Permissions */}
                <div>
                  <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                    Module Access Permissions
                  </h5>
                  <div className="space-y-2">
                    {modulePermissions.map((perm) => (
                      <label
                        key={perm.key}
                        className="flex items-center gap-2 text-sm text-gray-700"
                      >
                        <input
                          type="checkbox"
                          checked={!!form[perm.key]}
                          disabled
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 opacity-60 cursor-not-allowed"
                        />
                        <span className={form.role ? "" : "text-gray-400"}>
                          {perm.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Enable Portal Access */}
                <div className="pt-2 border-t">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => handleChange("isActive", e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>Enable portal access</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    This can be toggled independently to enable/disable user access.
                  </p>
                </div>
              </div>
              {!form.role && (
                <p className="mt-2 text-xs text-gray-500 italic">
                  Select a role above to see the permissions that will be assigned.
                </p>
              )}
            </div>

            <div className="md:col-span-2 flex justify-stretch sm:justify-end mt-2">
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-md theme-bg-primary px-4 py-2.5 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 theme-ring-primary focus:ring-offset-1 w-full sm:w-auto"
                disabled={creating}
              >
                {creating ? "Inviting..." : "Invite User"}
              </button>
            </div>
          </form>
        </section>

        {/* Existing Users Table */}
        <section>
          <h3 className="text-sm font-semibold text-gray-800 mb-3">
            Organisation Users
          </h3>
          {visibleUsers.length === 0 ? (
            <p className="text-sm text-gray-500">
              No users found for this organisation yet.
            </p>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="min-w-full text-xs sm:text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">User</th>
                    <th className="px-3 py-2 text-left font-medium">Role</th>
                    <th className="px-3 py-2 text-center font-medium">
                      Enable Access
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {visibleUsers.map((user) => (
                    <tr key={user._id}>
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium text-gray-900">
                          {user.name || "—"}
                        </div>
                        <div className="text-gray-600 text-xs">
                          {user.email || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-col gap-1">
                          <select
                            value={getCurrentRoleId(user)}
                            onChange={(e) =>
                              handleRoleChange(user, e.target.value)
                            }
                            disabled={saving || rolesLoading}
                            className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white"
                          >
                            <option value="">Select role...</option>
                            {assignableRoles.map((role) => (
                              <option key={role._id} value={role._id}>
                                {role.name}
                              </option>
                            ))}
                          </select>
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium w-max ${
                              user.isActive
                                ? "bg-green-50 text-green-700"
                                : "bg-red-50 text-red-700"
                            }`}
                          >
                            {user.isActive ? "Access enabled" : "Access disabled"}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!user.isActive}
                          onChange={() => handleToggle(user, "isActive")}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => handleRemove(user)}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
      {/* Delete Confirmation Dialog */}
      {deleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m-7 0a2 2 0 012-2h4a2 2 0 012 2m-8 0h8"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  Remove Organisation User
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Are you sure you want to remove this user from your organisation?
                </p>
              </div>
            </div>

            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4 space-y-1">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  User
                </p>
                <p className="text-sm text-gray-900">
                  {deleteDialog.userName}
                </p>
                <p className="text-xs text-gray-600">
                  {deleteDialog.userEmail}
                </p>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                disabled={saving}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={saving}
                className="px-4 py-2.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                {saving ? "Removing..." : "Yes, Remove User"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Enable / Disable Access Confirmation Dialog */}
      {accessDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-yellow-100">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-yellow-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {accessDialog.nextStatus
                    ? "Enable User Access"
                    : "Disable User Access"}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {accessDialog.nextStatus
                    ? "Are you sure you want to enable portal access for this user?"
                    : "Are you sure you want to disable portal access for this user?"}
                </p>
              </div>
            </div>

            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4 space-y-1">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  User
                </p>
                <p className="text-sm text-gray-900">{accessDialog.userName}</p>
                <p className="text-xs text-gray-600">{accessDialog.userEmail}</p>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                onClick={() => setAccessDialog(null)}
                disabled={saving}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!accessDialog) return;
                  const { userId, nextStatus } = accessDialog;
                  const updates = {
                    isActive: nextStatus,
                    isAccessEnabled: nextStatus,
                  };
                  const result = await updateUser(userId, updates);
                  if (result) {
                    setSuccessMessage(
                      nextStatus
                        ? "User access has been enabled successfully."
                        : "User access has been disabled successfully."
                    );
                    setTimeout(() => setSuccessMessage(""), 3000);
                  }
                  setAccessDialog(null);
                }}
                disabled={saving}
                className="px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                {saving
                  ? "Saving..."
                  : accessDialog.nextStatus
                  ? "Yes, Enable"
                  : "Yes, Disable"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;

